import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Segmented } from 'antd';
import { appMessage as message } from '../utils/appMessage';

import { DashboardLayout } from '../layouts/DashboardLayout';
import { fetchRegistrableExpansions, registerProducts } from '../api/api';
import {
  ExpansionOption, RegistrableGameType, GAME_TYPE_LABEL,
  REGISTER_PLACEHOLDER_PRICE,
} from '../types/productRegistration';
import { SPACING, FONT_SIZE } from '../constants/constants';
import { formatPrice } from '../utils/format';
import { useThemeTokens } from '../hooks/UseThemeTokens';
import { useJobRunner } from '../hooks/UseJobRunner';
import { useProductRegistrationJob } from '../contexts/ProductRegistrationJobContext';
import { AppTabBar } from '../components/common/AppTabBar';
import { AppLoadError } from '../components/common/AppLoadError';
import { JobModal } from '../components/modal/JobModal';
import {
  ExpansionRegisterPanel, ExpansionRegisterSummary,
} from '../components/expansionRegisterPanel/ExpansionRegisterPanel';
import { SearchRegisterPanel, QueueRegisterSummary } from '../components/searchRegisterPanel/SearchRegisterPanel';
import { RegisterConfirmModal, ConfirmRow } from '../components/registerConfirmModal/RegisterConfirmModal';

const GAME_OPTIONS: { label: string; value: RegistrableGameType }[] =
  (['POKEMON', 'ONE_PIECE', 'DIGIMON', 'HOLOLIVE'] as RegistrableGameType[])
    .map((value) => ({ value, label: GAME_TYPE_LABEL[value] }));

type PageMode = 'expansion' | 'search';

const MODE_TABS: { key: PageMode; label: string }[] = [
  { key: 'expansion', label: '확장팩 일괄 등록' },
  { key: 'search', label: '검색 개별 등록' },
];

// ─── 확인 모달 상태 ───────────────────────────────────────────────────────────

type ConfirmState =
  | ({ mode: 'pack' } & ExpansionRegisterSummary)
  | ({ mode: 'queue' } & QueueRegisterSummary)
  | null;

const initialStateLine = `판매중지 · 자리표시 가격 ${formatPrice(REGISTER_PLACEHOLDER_PRICE)}`;

/**
 * 신상품 등록 — 게임을 고른 뒤 탭(확장팩 일괄 / 검색 개별) 중 하나로 등록한다. 두 탭 모두
 * 실행 전 무엇이 몇 건 생기는지 미리보기 → 확인 모달 → 등록 순서다("상품 등록 개선안",
 * 2026-08-31) — 네이버 스마트스토어에 실제 상품을 만드는 되돌리기 어려운 쓰기 작업이라
 * 실행 전 확인을 강제한다. 판매중지·자리표시 가격(9,990원)·초기 재고 1로 시작하는 동작은
 * 그대로다.
 *
 * 잡 진행 상태(activeJobId/detailJob)는 카드 관리의 CardJobBar와 마찬가지로 MainApp
 * 최상위(ProductRegistrationJobContext)에서 관리한다 — 등록 도중 다른 페이지로 이동했다
 * 돌아와도 진행바가 사라지지 않아야 한다.
 */
export const ProductRegistrationPage: React.FC = () => {
  const t = useThemeTokens();
  const [mode, setMode] = useState<PageMode>('expansion');
  const [gameType, setGameType] = useState<RegistrableGameType>('ONE_PIECE');

  const [expansions, setExpansions] = useState<ExpansionOption[]>([]);
  const [loadingExpansions, setLoadingExpansions] = useState(true);
  const [expansionError, setExpansionError] = useState<Error | null>(null);
  const [selectedCode, setSelectedCode] = useState<string | null>(null);

  const [confirm, setConfirm] = useState<ConfirmState>(null);
  const [starting, setStarting] = useState(false);

  // 게임 전환·등록 완료 시 SearchRegisterPanel을 리마운트해 대기 목록·검색 상태를 비운다.
  const [searchResetNonce, setSearchResetNonce] = useState(0);

  // 확장팩 조회 응답 순서 가드 — 마지막 요청만 반영(늦게 온 옛 응답이 최신 목록을 덮지 않게).
  const expansionsSeqRef = useRef(0);

  const { activeJobId, setActiveJobId, detailJob, setDetailJob } = useProductRegistrationJob();
  const runJob = useJobRunner(setActiveJobId);

  // 등록 잡이 이미 도는 중이면 새 등록을 못 열게 막는다(2026-09-01 실사고 — 이중 클릭·재시도로
  // 같은 카드가 3번 등록돼 네이버에 중복 상품이 생김). 백엔드에도 같은 매장 동시 등록을
  // 거부하는 가드가 있지만, 여기서 먼저 막아 사용자가 확인 모달까지 가지 않게 한다.
  const registerBusy = activeJobId != null;
  const openConfirm = useCallback((next: NonNullable<ConfirmState>) => {
    if (registerBusy) {
      message.warning('상품 등록 작업이 진행 중입니다. 완료된 뒤 다시 시도하세요.');
      return;
    }
    setConfirm(next);
  }, [registerBusy]);

  const loadExpansions = useCallback((game: RegistrableGameType) => {
    // 응답 순서 가드(2026-10-20 리뷰): 게임을 빠르게 바꾸면 늦게 도착한 옛 요청의 응답이
    // 최신 목록을 덮어쓸 수 있다 — 마지막 요청의 응답만 반영한다.
    const seq = ++expansionsSeqRef.current;
    setLoadingExpansions(true);
    setExpansionError(null);
    fetchRegistrableExpansions(game)
      .then((list) => { if (seq === expansionsSeqRef.current) setExpansions(list); })
      .catch((e: unknown) => {
        if (seq !== expansionsSeqRef.current) return;
        // 원인(백엔드 오류 메시지·네트워크 오류)을 버리지 않고 그대로 보존한다 —
        // 예전엔 고정 문구만 남겨 AppLoadError 에 왜 실패했는지가 안 보였다(2026-10-19 리뷰).
        message.error('확장팩 목록을 불러오지 못했습니다');
        setExpansionError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => { if (seq === expansionsSeqRef.current) setLoadingExpansions(false); });
  }, []);

  useEffect(() => {
    setSelectedCode(null);
    setSearchResetNonce((n) => n + 1);
    loadExpansions(gameType);
  }, [gameType, loadExpansions]);

  // 검색 탭에 갔다가 확장팩 탭으로 돌아왔는데 이전 조회가 실패한 상태면 한 번 더 시도한다
  // (2026-10-19 리뷰) — 예전엔 게임을 바꾸지 않으면 재조회가 안 돼 오류 화면에 갇혔다.
  // expansionError 를 의존성에 넣으면 실패 → 오류 설정 → 재시도 → 실패가 무한 반복되므로
  // mode 변화에만 반응한다(gameType 변경은 위 useEffect 가 이미 처리한다).
  // 조회가 아직 진행 중일 때 탭을 왕복하면 같은 요청이 중복으로 나가므로 건너뛴다
  // (2026-10-19 리뷰) — 진행 중 요청의 응답이 오류면 그때 오류 화면에서 다시 시도할 수 있다.
  useEffect(() => {
    if (mode === 'expansion' && expansionError && !loadingExpansions) loadExpansions(gameType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // 페이지를 보고 있는 동안 등록 잡이 완료되면 대기 목록·확장팩 선택을 비운다
  // (언마운트돼 있던 동안 완료된 경우는 재마운트 시 빈 상태로 시작하므로 무관).
  useEffect(() => {
    if (detailJob?.status === 'DONE') {
      setSelectedCode(null);
      setSearchResetNonce((n) => n + 1);
    }
  }, [detailJob]);

  const handleConfirm = useCallback(async () => {
    if (!confirm) return;
    if (activeJobId != null) {
      message.warning('상품 등록 작업이 진행 중입니다. 완료된 뒤 다시 시도하세요.');
      setConfirm(null);
      return;
    }
    setStarting(true);
    const ok = await runJob(
      () => confirm.mode === 'pack'
        ? registerProducts({ gameType, expansionCode: confirm.expansionCode, stockByCode: confirm.stockByCode })
        : registerProducts({ gameType, productCodes: confirm.productCodes, stockByCode: confirm.stockByCode }),
      '상품 등록을 시작하지 못했습니다',
    );
    setStarting(false);
    if (ok) setConfirm(null);
  }, [confirm, gameType, runJob, activeJobId]);

  const confirmProps = useMemo(() => {
    if (!confirm) return null;
    if (confirm.mode === 'pack') {
      const rows: ConfirmRow[] = [
        { label: '확장팩', value: confirm.expansionLabel },
        { label: '새로 생성', value: `${confirm.newCount.toLocaleString()}건`, emphasis: 'accent' },
        { label: '건너뜀', value: `${confirm.dupCount.toLocaleString()}건 (이미 등록됨)`, emphasis: 'muted' },
        { label: '총 재고', value: `${confirm.totalStock.toLocaleString()}장` },
        { label: '초기 상태', value: initialStateLine },
      ];
      return {
        title: '확장팩 상품을 생성합니다',
        intro: '네이버 스마트스토어에 새 상품이 실제로 생성됩니다. 아래 내용을 확인하세요.',
        rows,
        confirmLabel: `${confirm.newCount.toLocaleString()}건 생성`,
      };
    }
    const rows: ConfirmRow[] = [
      { label: '등록 건수', value: `${confirm.count.toLocaleString()}건`, emphasis: 'accent' },
      { label: '총 재고', value: `${confirm.totalStock.toLocaleString()}장` },
      { label: '초기 상태', value: initialStateLine },
    ];
    return {
      title: '대기 목록을 등록합니다',
      intro: '담아 둔 카드를 네이버 스마트스토어에 신상품으로 생성합니다.',
      rows,
      confirmLabel: `${confirm.count.toLocaleString()}건 생성`,
    };
  }, [confirm]);

  return (
    <DashboardLayout title="상품 등록">
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm, marginBottom: SPACING.lg }}>
        <span style={{ fontSize: FONT_SIZE.sm, color: t.textSecondary }}>작업 대상</span>
        <Segmented
          value={gameType}
          onChange={(v) => setGameType(v as RegistrableGameType)}
          options={GAME_OPTIONS}
        />
      </div>

      <AppTabBar tabs={MODE_TABS} active={mode} onChange={setMode} />

      {mode === 'expansion' ? (
        expansionError ? (
          <AppLoadError message={expansionError.message} error={expansionError} onRetry={() => loadExpansions(gameType)} />
        ) : (
          <ExpansionRegisterPanel
            gameType={gameType}
            expansions={expansions}
            loadingExpansions={loadingExpansions}
            selectedCode={selectedCode}
            onSelectCode={setSelectedCode}
            busy={registerBusy}
            onRegisterClick={(summary) => openConfirm({ mode: 'pack', ...summary })}
          />
        )
      ) : (
        <SearchRegisterPanel
          key={`${gameType}-${searchResetNonce}`}
          gameType={gameType}
          busy={registerBusy}
          onRegisterClick={(summary) => openConfirm({ mode: 'queue', ...summary })}
        />
      )}

      {confirmProps && (
        <RegisterConfirmModal
          open={confirm != null}
          title={confirmProps.title}
          intro={confirmProps.intro}
          rows={confirmProps.rows}
          confirmLabel={confirmProps.confirmLabel}
          confirming={starting}
          onConfirm={handleConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}

      <JobModal job={detailJob} open={detailJob != null} onClose={() => setDetailJob(null)} />
    </DashboardLayout>
  );
};
